/**
 * Vertical video carousel (blocks/acti-video-carousel.liquid).
 * Videos play inline only on tap/click or keyboard, start muted, and only one plays at a time.
 */

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class ActiVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.toggleButton = this.querySelector('.acti-video__toggle');
    this.soundButton = this.querySelector('.acti-video__sound');
    if (!this.video || !this.toggleButton) return;

    this.toggleButton.addEventListener('click', () => (this.video.paused ? this.play() : this.pause()));
    this.soundButton?.addEventListener('click', () => this.setMuted(!this.video.muted));
    this.video.addEventListener('play', () => this.sync());
    this.video.addEventListener('pause', () => this.sync());
    this.video.addEventListener('ended', () => this.sync());
  }

  play() {
    document.querySelectorAll('acti-video').forEach((other) => {
      if (other !== this) other.pause();
    });
    if (this.soundButton) this.soundButton.hidden = false;
    this.video.play().catch(() => this.sync());
  }

  pause() {
    this.video?.pause();
  }

  setMuted(muted) {
    if (!muted) {
      // Only one video can have sound
      document.querySelectorAll('acti-video').forEach((other) => {
        if (other !== this) other.setMuted(true);
      });
    }
    this.video.muted = muted;
    if (this.soundButton) {
      this.soundButton.setAttribute('aria-pressed', String(!muted));
      this.soundButton.setAttribute('aria-label', muted ? 'Activar sonido' : 'Silenciar');
    }
  }

  sync() {
    const playing = !this.video.paused && !this.video.ended;
    this.toggleAttribute('playing', playing);
    this.toggleButton.setAttribute('aria-label', playing ? this.dataset.labelPause : this.dataset.labelPlay);
  }
}

class ActiVideoCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.acti-videos__track');
    this.arrows = [...this.querySelectorAll('.acti-videos__arrow')];
    if (!this.track) return;

    this.arrows.forEach((arrow) =>
      arrow.addEventListener('click', () => this.scrollByCard(Number(arrow.dataset.direction)))
    );
    this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true });
    window.addEventListener('resize', () => this.updateArrows(), { passive: true });
    this.updateArrows();
  }

  scrollByCard(direction) {
    const card = this.track.querySelector('.acti-videos__item');
    if (!card) return;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    this.track.scrollBy({
      left: direction * (card.getBoundingClientRect().width + gap),
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  updateArrows() {
    const [previous, next] = this.arrows;
    if (!previous || !next) return;
    const { scrollLeft, clientWidth, scrollWidth } = this.track;
    previous.disabled = scrollLeft <= 2;
    next.disabled = scrollLeft + clientWidth >= scrollWidth - 2;
  }
}

if (!customElements.get('acti-video')) customElements.define('acti-video', ActiVideo);
if (!customElements.get('acti-video-carousel')) customElements.define('acti-video-carousel', ActiVideoCarousel);
